import type { PortalMessage } from '@central/api';
import { charCount, color, font, formatAgo } from '@central/ui';
import { Alert, Button, FileInput, Textarea } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../api';
import { errorMessage, fileSize } from '../format';
import { fileProblem, uploadFiles } from './attachments';
import { MAX_BODY } from './messageRules';
import { authorLabel } from './threadLabels';

const eventVerb = { resolved: 'Resolved', reopened: 'Reopened' } as const;

/** One post, or a resolve or reopen event. Rethink's posts sit on a blue surface. */
export function MessageItem({ m }: { m: PortalMessage }) {
  if (m.event) {
    return (
      <li
        data-event={m.event}
        style={{ fontSize: 12, textAlign: 'center', color: color.textMuted }}
      >
        <b style={{ color: color.text }}>
          {eventVerb[m.event]} by {authorLabel(m.author)}
        </b>{' '}
        · {formatAgo(m.at)}
      </li>
    );
  }
  const system = m.author.kind === 'system';
  const staff = m.author.kind === 'staff';
  return (
    <li
      data-author={m.author.kind}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        fontSize: 14,
        lineHeight: '21px',
        padding: '10px 14px',
        borderRadius: 8,
        border: `1px solid ${staff ? color.portalNavActiveBg : color.rowRule}`,
        background: staff ? color.portalNavActiveBg : color.surface,
      }}
    >
      <span style={{ fontSize: 12, color: color.textMuted }}>
        <b style={{ color: staff ? color.portalNavActiveText : color.text }}>
          {authorLabel(m.author)}
        </b>{' '}
        · {formatAgo(m.at)}
      </span>
      <div
        style={
          system
            ? {
                fontFamily: font.mono,
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                overflowWrap: 'anywhere',
              }
            : { whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }
        }
      >
        {m.body}
      </div>
      {m.attachments.length > 0 && (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
          {m.attachments.map((a) => (
            <li key={a.key}>
              {a.url ? (
                <a href={a.url} rel="noreferrer">
                  {a.name}
                </a>
              ) : (
                a.name
              )}{' '}
              <span style={{ color: color.textMuted }}>{fileSize(a.bytes)}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

export function Reply({ threadId, resolved }: { threadId: string; resolved: boolean }) {
  const qc = useQueryClient();
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [problem, setProblem] = useState<string>();
  const send = useMutation({
    mutationFn: async () => {
      const attachments = files.length ? await uploadFiles(files) : undefined;
      return api.threads.reply(threadId, { body: body.trim(), attachments });
    },
    onSuccess: async () => {
      setBody('');
      setFiles([]);
      await qc.invalidateQueries({ queryKey: ['threads'] });
    },
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const b = body.trim();
    const p = !b
      ? 'Write a message.'
      : charCount(b) > MAX_BODY
        ? 'Keep the message under 20,000 characters.'
        : fileProblem(files);
    setProblem(p);
    if (!p) send.mutate();
  };
  return (
    <form
      onSubmit={submit}
      noValidate
      style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <Textarea
        label="Reply"
        description={resolved ? 'Replying reopens this conversation.' : undefined}
        autosize
        minRows={3}
        value={body}
        onChange={(e) => setBody(e.currentTarget.value)}
      />
      <FileInput
        label="Files"
        placeholder="Choose files"
        multiple
        clearable
        value={files}
        onChange={setFiles}
      />
      {(problem || send.error) && <Alert color="red">{problem ?? errorMessage(send.error)}</Alert>}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="submit" loading={send.isPending}>
          Send reply
        </Button>
      </div>
    </form>
  );
}
